import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  FormControl,
  MenuItem,
  Select,
} from "@mui/material";
import { getPurchaseOrderById, updatePurchaseOrder } from "../../Services/PurchaseOrderService";
import { useLocation, useNavigate } from "react-router-dom";
import { POItem } from "../../Interfaces/IPurchaseOrder";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
const EditPurchaseOrder = () => {
  const navigate = useNavigate();
  const [po, setPo] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [items, setItems] =useState<POItem[]>([]);
  const { state } = useLocation();

  const poId = state?.poId;
  const poNumber = state?.poNumber;
  const orgId = state?.orgId;
  const financialYear = state?.financialYear;

useEffect(() => {
  const fetchPo = async () => {
    if (!poNumber || !orgId || !financialYear) return;

    const data = await getPurchaseOrderById({
      poNumber,
      orgId,
      financialYear,
    });
    const normalizedItems = (data.items || []).map(calculateItem);
    setPo(data);
    setItems(normalizedItems);
    setLoading(false);
  };

  fetchPo();
}, [poNumber, orgId, financialYear]);


    const updateField = (field: string, value: string) => {
    setPo((prev: any) => ({
        ...(prev || {}),
        [field]: value,
    }));
    };

  const updateItem = (index: number, field: keyof POItem, value: string) => {
  const updated = [...items];

  updated[index] = {
    ...updated[index],
    [field]: value,
  };

  const qty = Number(updated[index].quantity) || 0;
  const price = Number(updated[index].unitPrice) || 0;
  const discount = Number(updated[index].discount) || 0;
  const tax = Number(updated[index].taxRate) || 0;

  const amount = qty * price;
  const discountAmt = (amount * discount) / 100;
  const taxable = amount - discountAmt;
  const taxAmt = (taxable * tax) / 100;
  const total = taxable + taxAmt;

  updated[index].amount = amount;
  updated[index].discountAmt = discountAmt;
  updated[index].taxAmount = taxAmt;
  updated[index].lineTotal = total;

  setItems(updated);
};

    const calcTotals = () => {
    const subtotal = items.reduce((s, i) => s + (i.amount || 0), 0);
    const discountTotal = items.reduce((s, i) => s + (i.discountAmt || 0), 0);
    const taxTotal = items.reduce((s, i) => s + (i.taxAmount || 0), 0);
    const grandTotal = items.reduce((s, i) => s + (i.lineTotal || 0), 0);

    return { subtotal, discountTotal, taxTotal, grandTotal };
  };

  const calculateItem = (item: POItem): POItem => {
  const qty = Number(item.quantity) || 0;
  const price = Number(item.unitPrice) || 0;
  const discount = Number(item.discount) || 0;
  const tax = Number(item.taxRate) || 0;

  const amount = qty * price;
  const discountAmt = (amount * discount) / 100;
  const taxable = amount - discountAmt;
  const taxAmount = (taxable * tax) / 100;
  const lineTotal = taxable + taxAmount;

  return {
    ...item,
    amount,
    discountAmt,
    taxAmount,
    lineTotal,
  };
};


  const handleUpdate = async () => {
    try {
      const totals = calcTotals();
      const poData = {
      vendorId: po.vendorId,
      vendorName: po.vendorName,
      poDate: po.poDate,
      billingAddress: po.billingAddress,
      shippingAddress: po.shippingAddress,
      paymentTerms: po.paymentTerms,
      expectedDeliveryDate: po.expectedDeliveryDate,
      financialYear: po.financialYear,
      status: po.status,
      totalAmount: totals.subtotal,
      totalTax: totals.taxTotal,
      discount: totals.discountTotal,
      grandTotal: totals.grandTotal,
    };
      await updatePurchaseOrder(poId,
      poData,
      items
    );

      alert("PO Updated Successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to update PO");
    }
  };
  const handleBackClick = () => {
    navigate("/dashboard/purchase/view-purchase-orders");
  };
  if (loading) return <Typography>Loading...</Typography>;

  return (
    <>
      <div className="page-location">
        <span>Purchase &gt; Edit Purchase Order</span>
      </div>
       <button onClick={handleBackClick} className="back-button">
        <i className="fas fa-arrow-left back-icon"></i>
        Back
      </button>
    <Box p={2} sx={{width: "100%"}}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField fullWidth label="PO Number" value={po.poNumber} disabled />
          </Grid>
          <Grid item xs={4}>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
  <DatePicker
    label="PO Date"
    format="DD/MM/YYYY"
    value={
      po.poDate
        ? dayjs(po?.poDate)
        : null
    }
    onChange={(value) =>
      updateField(
        "poDate",
        value ? value.toISOString() : ""
      )
    }
    slotProps={{ textField: { fullWidth: true } }}
  />
</LocalizationProvider>
          </Grid>
          <Grid item xs={4}>
            <TextField fullWidth label="Vendor Name" value={po.vendorName} disabled />
          </Grid>

          <Grid item xs={6}>
            <TextField fullWidth label="Billing Address" value={po.billingAddress} onChange={(e) => updateField("billingAddress", e.target.value)} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth label="Shipping Address" value={po.shippingAddress} onChange={(e) => updateField("shippingAddress", e.target.value)} />
          </Grid>

          <Grid item xs={4}>
            <TextField
             select
             fullWidth
             label="Payment Terms"
             value={po.paymentTerms}
             onChange={(e) => updateField("paymentTerms", e.target.value)}
             >
            <MenuItem value="Advance Payment">Advance Payment</MenuItem>
            <MenuItem value="Net 15">Net 15</MenuItem>
            <MenuItem value="Net 30">Net 30</MenuItem>
            <MenuItem value="Net 60">Net 60</MenuItem>
            <MenuItem value="Partial Payment">Partial Payment</MenuItem>
            </TextField>

          </Grid>
          <Grid item xs={4}>
           <LocalizationProvider dateAdapter={AdapterDayjs}>
  <DatePicker
    label="Expected Delivery"
    format="DD/MM/YYYY"
    value={
      po.expectedDeliveryDate
        ? dayjs(po?.expectedDeliveryDate)
        : null
    }
    onChange={(value) =>
      updateField(
        "expectedDeliveryDate",
        value ? value.toISOString() : ""
      )
    }
    slotProps={{ textField: { fullWidth: true } }}
  />
</LocalizationProvider>
          </Grid>
          <Grid item xs={4}>
           <FormControl fullWidth>
             <InputLabel>Status</InputLabel>
             <Select
             value={po.status || ""}
            label="Status"
            onChange={(e) => updateField("status", e.target.value)}
            >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="close">Close</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            
            </Select>
            </FormControl>

          </Grid>
        </Grid>
      </Paper>

      {/* ITEMS SECTION */}
  <Paper sx={{ p: 2, mb: 3 }}>
<Typography
    variant="h6"
    sx={{
      fontWeight: 600,
      color: "#1976d2",
      letterSpacing: 0.5,
    }}
  >
    Items
  </Typography>

  {items.map((item, index) => (
    <Paper
      key={item.itemId}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 2,
      }}
    >
     <Typography variant="subtitle1" fontWeight="bold" mb={1}>
      {index + 1}. {item.itemName}
    </Typography>

      <Grid container spacing={2}>
        {/* Quantity */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Quantity"
            type="number"
            value={item.quantity}
          inputProps={{
            inputMode: "numeric",
            pattern: "[1-9][0-9]*",
          }}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              updateItem(index, "quantity", "");
              return;
            }
            if (/^[1-9]\d*$/.test(value)) {
              updateItem(index, "quantity", value);
            }
          }}
          />
        </Grid>
        {/* Rate */}
        <Grid item xs={12} sm={6} md={3}>
        <TextField
        fullWidth
        size="small"
        label="Rate"
        value={item.unitPrice}
        inputProps={{
          inputMode: "decimal",
        }}
        onChange={(e) => {
          const value = e.target.value;

          // Allow empty (user deleting)
          if (value === "") {
            updateItem(index, "unitPrice", "");
            return;
          }

          // Allow positive number with up to 2 decimals
          if (/^\d+(\.\d{0,2})?$/.test(value)) {
            updateItem(index, "unitPrice", value);
          }
        }}
      />

        </Grid>
        {/* Amount */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Amount"
            value={item.amount?.toFixed(2)}
            disabled
          />
        </Grid>

        {/* Discount % */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Discount %"
            type="number"
            value={item.discount}
            onChange={(e) =>
              updateItem(index, "discount", e.target.value)
            }
          />
        </Grid>

        {/* Discount Amount */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Discount Amount"
            value={item.discountAmt?.toFixed(2)}
            disabled
          />
        </Grid>

        {/* GST % */}
        <Grid item xs={12} sm={6} md={3}>
             <TextField         size="small"
                                select
                                label="GST %"
                                fullWidth
                                value={item.taxRate}
                                onChange={(e) =>
                                   updateItem(index, "taxRate", e.target.value)
                                }
                              >
                                {[0, 5, 12, 18, 28].map((gst) => (
                                  <MenuItem key={gst} value={gst}>
                                    {gst}%
                                  </MenuItem>
                                ))}
             </TextField>
        </Grid>

        {/* Tax Amount */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Tax Amount"
            value={item.taxAmount?.toFixed(2)}
            disabled
          />
        </Grid>

        {/* Line Total */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Total"
            value={item.lineTotal?.toFixed(2)}
            disabled
          />
        </Grid>
      </Grid>
    </Paper>
  ))}
  </Paper>

  <Box
  mt={3}
  p={2}
  component={Paper}
  sx={{
    width: "100%",
  }}
>
  {(() => {
    const totals = calcTotals();
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography>
            Subtotal: ₹ {totals.subtotal.toFixed(2)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            Total Discount: ₹ {totals.discountTotal.toFixed(2)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography>
            Total Tax: ₹ {totals.taxTotal.toFixed(2)}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Grand Total: ₹ {totals.grandTotal.toFixed(2)}
          </Typography>
        </Grid>
      </Grid>
    );
  })()}
</Box>

      <Box mt={3}>
        <Button variant="contained" color="primary" onClick={handleUpdate}>
          Update PO
        </Button>
      </Box>
    </Box>
    </>
  );
};

export default EditPurchaseOrder;
